import { useRef, useState, useEffect } from "react";
import { useDarkMode } from "../hooks/useDarkMode";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FaEdit, FaSave, FaTimes, FaPlus, FaFilePdf, FaFileImage, FaUserCircle } from "react-icons/fa";
import { updatePatientDetails, fetchPatientByPhone, registerPatient } from "../Services/api";
import { toast } from 'react-toastify';
import Loader from './Loader';
import { Features } from "../config/Features";


const reactSelectStyles = (isDark, isReadOnly) => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: isReadOnly
      ? isDark
        ? "#374151" // match dark:bg-gray-700 for readonly
        : "#f9fafb" // match bg-gray-50 for readonly
      : isDark
        ? "#1f2937" // normal dark mode editable
        : "#ffffff", // normal light mode editable
    cursor: isReadOnly ? "not-allowed" : "pointer",
    borderColor: state.isFocused
      ? "#3b82f6"
      : isDark
        ? "#4b5563"
        : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    zIndex: 9999,
  }),

  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? isDark
        ? "#374151"
        : "#e5e7eb"
      : state.isSelected
        ? "#2563eb"
        : isDark
          ? "#1f2937"
          : "#ffffff",
    color: state.isSelected ? "#ffffff" : isDark ? "#f9fafb" : "#111827",
    cursor: "pointer",
  }),

  singleValue: (base) => ({
    ...base,
    color: isReadOnly
      ? isDark
        ? "#9ca3af" // dimmed text for readonly dark
        : "#6b7280" // dimmed text for readonly light
      : isDark
        ? "#f9fafb"
        : "#111827",
  }),

  input: (base) => ({
    ...base,
    color: isReadOnly
      ? isDark
        ? "#9ca3af"
        : "#6b7280"
      : isDark
        ? "#f9fafb"
        : "#111827",
  }),

  placeholder: (base) => ({
    ...base,
    color: isDark ? "#9ca3af" : "#6b7280",
  }),
});




export default function PersonalDetails({ form, onChange, isPrefilled, masterLists, onShowOpCard, }) {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (isPrefilled) {
      setCollapsed(true);
    }
  }, [isPrefilled]);
  const [errors, setErrors] = useState({});
  const isDark = useDarkMode();

  const [editMode, setEditMode] = useState(false);
  const [showShowHide, setShowShowHide] = useState(true);

  const [localForm, setLocalForm] = useState({ ...form });
  const [originalForm, setOriginalForm] = useState({ ...form });
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const { t } = useTranslation();
  const idProofOptions = ["Aadhaar Card", "PAN Card", "Voter ID", "Passport"];
  const fileInputRef = useRef(null);
  const [idPreview, setIdPreview] = useState(null);
  const genders = masterLists?.genders ?? [];
  const countries = masterLists?.countries ?? [];
  const states = masterLists?.states ?? [];
  const districts = masterLists?.districts ?? [];
  const cities = masterLists?.cities ?? [];
  const [isAdding, setIsAdding] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const getFileIcon = (fileName) => {
    if (!fileName) return <FaFilePdf className="text-gray-500" />;
    const ext = fileName.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return <FaFileImage className="text-blue-500" />;
    if (ext === "pdf") return <FaFilePdf className="text-red-500" />;
    return <FaFilePdf className="text-gray-500" />;
  };
  const hasStoredPatients = (() => {
    try {
      const raw = localStorage.getItem("patients");
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) && list.length > 0;
    } catch {
      return false;
    }
  })();
  const prevDistrict = useRef(localForm.district_id);

  useEffect(() => {
    if (prevDistrict.current && prevDistrict.current !== localForm.district_id) {
      handleFieldChange("city_id", "");
      handleFieldChange("city", "");
    }
    prevDistrict.current = localForm.district_id;
  }, [localForm.district_id]);
  // File input change handler
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Upload / save file name
    onChange({ target: { name: "idProof", value: file.name } });

    // Create blob URL for preview
    const previewUrl = URL.createObjectURL(file);
    setIdPreview(previewUrl);

    // Open modal automatically if you want preview popup
    setIsModalOpen(true);
  };

  const isReadOnly = isPrefilled && !editMode;

  const readOnlyProps = isReadOnly ? { readOnly: true, disabled: true } : {};
  const selectDisabled = isReadOnly;

  useEffect(() => {
    if (!localForm.country_id && countries.length > 0) {
      const defaultCountry = countries.find(c => c.is_default === true);
      const defaultState = states.find(c => c.is_default === true);

      if (defaultCountry) {
        handleFieldChange("country_id", defaultCountry.country_id);
        handleFieldChange("country", defaultCountry.country_name);
      }
      if (defaultState) {
        handleFieldChange("state_id", defaultState.state_id);
        handleFieldChange("state", defaultState.state_name);
      }
    }
  }, [countries]);



  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (file) onChange({ target: { name: "idProof", value: file.name } });
  };
  const nameRegex = /^[a-zA-Z]+([ '-]?[a-zA-Z]+){0,2}$/;
  const relationRegex = /^[a-zA-Z\s.]{2,}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const mobileRegex = /^[0-9]{10}$/;
  const pincodeRegex = /^[0-9]{6}$/;

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };
  const handleDOBChange = (e) => {
    const dob = e.target.value;

    const updated = {
      dob,
      age: calculateAge(dob)
    };

    setLocalForm((prev) => ({ ...prev, ...updated }));

    onChange({ target: { name: "dob", value: dob } });
    onChange({ target: { name: "age", value: updated.age } });
  };
  const validateForm = () => {
    const newErrors = {};

    // Mandatory fields
    if (!localForm.firstName?.trim()) newErrors.firstName = t("personal.required");
    if (!localForm.address1?.trim()) newErrors.address1 = t("personal.required");
    if (!localForm.country_id) newErrors.country_id = t("personal.required");
    if (!localForm.state_id) newErrors.state_id = t("personal.required");
    if (!localForm.district_id) newErrors.district_id = t("personal.required");
    if (!localForm.city_id) newErrors.city_id = t("personal.required");
    if (!localForm.gender) newErrors.gender = t("personal.required");
    // Optional validations
    if (localForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(localForm.email)) {
      newErrors.email = t("personal.invalidEmail");
    }
    if (!localForm.pincode?.trim()) {
      newErrors.pincode = t("personal.required");
    } else if (!/^[0-9]{6}$/.test(localForm.pincode)) {
      newErrors.pincode = t("personal.invalidPincode");
    }

    setErrors(newErrors);

    // Form is valid only if no errors
    return Object.keys(newErrors).length === 0;
  };

  const validateField = (field, value) => {
    let error = "";
    if (["firstName"].includes(field)) {
      if (!value.trim()) error = t("personal.required");
      else if (!nameRegex.test(value)) error = t("personal.invalidName");
    } else if (field === "address1") {
      if (!value || !value.trim()) error = t("personal.required");
    } else if (field === "email") {
      if (value && !emailRegex.test(value)) error = t("personal.invalidEmail");
    } else if (field === "mobileNo") {
      if (!mobileRegex.test(value)) error = t("personal.invalidMobile");
    } else if (field === "pincode") {
      if (!pincodeRegex.test(value)) error = t("personal.invalidPincode");
    }
    else if (["country_id", "state_id", "district_id", "city_id"].includes(field)) {
      if (!value) error = t("personal.required"); // ✅ check for missing value
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === ""; // optional: return if valid

  };

  const handleFieldChange = (field, value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }));
    if (onChange) {
      onChange({ target: { name: field, value } });
    }
  };

  useEffect(() => {
    if (!isAdding) {
      setLocalForm({ ...form });
      setOriginalForm({ ...form });
    }

  }, [form]);


  const handleCancel = () => {
    // revert to original snapshot
    setLocalForm({ ...originalForm });
    setEditMode(false);
    setIsAdding(false);
    setCollapsed(true);
    setShowShowHide(true);
  };
  const SALUTATION_GENDER_MAP = {
    "Mr.": "Male",
    "Master.": "Male",
    "Brother of.": "Male",
    "S/o.": "Male",
    "B/O.": "Male",

    "Mrs.": "Female",
    "Ms.": "Female",
    "Miss.": "Female",
    "Mother of.": "Female",
    "W/O.": "Female",
    "D/o.": "Female",

    "Baby Boy of.": "Male",
    "Baby Girl of.": "Female",

    // Neutral / no auto gender
    "Dr.": null,
    "Prof.": null,
    "Adv.": null,
    "Capt.": null,
    "Major.": null,
    "Rev.": null,
    "Fr.": null,
    "Physio.": null,
    "Baby.": null,
    "Baby of.": null,
    "Hon.": null,
    "Mother In Law.": null,
    "C/o.": null,
    "Sr.": null
  };


  const salutationOptions = [
    { value: "Mr.", label: "Mr." },
    { value: "Mrs.", label: "Mrs." },
    { value: "Ms.", label: "Ms." },
    { value: "Miss.", label: "Miss." },
    { value: "Dr.", label: "Dr." },
    { value: "Prof.", label: "Prof." },
    { value: "Adv.", label: "Adv." },
    { value: "Capt.", label: "Capt." },
    { value: "Major.", label: "Major." },
    { value: "Rev.", label: "Rev." },
    { value: "Fr.", label: "Fr." },
    { value: "Hon.", label: "Hon." },
    { value: "Master.", label: "Master." },
    { value: "Baby.", label: "Baby." },
    { value: "Baby Boy of.", label: "Baby Boy of." },
    { value: "Baby Girl of.", label: "Baby Girl of." },
    { value: "Baby of.", label: "Baby of." },
    { value: "Mother of.", label: "Mother of." },
    { value: "Mother In Law.", label: "Mother In Law." },
    { value: "Brother of.", label: "Brother of." },
    { value: "S/o.", label: "S/o." },
    { value: "D/o.", label: "D/o." },
    { value: "C/o.", label: "C/o." },
    { value: "B/O.", label: "B/O." },
    { value: "Sr.", label: "Sr." },
    { value: "Physio.", label: "Physio." },
    { value: "W/O.", label: "W/O." }
  ];

  const normalizeSalutation = (s) => {
    if (!s) return "";
    return s.endsWith(".") ? s : `${s}.`;
  };
  const scrollToFirstError = (errors) => {
    const firstKey = Object.keys(errors)[0];
    if (firstKey) {
      document.querySelector(`[name="${firstKey}"]`)?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      scrollToFirstError(errors);

      toast.error(t("personal.fixErrors")); // optional toast
      return;
    }
    try {

      setLoadingMessage("Updating records...");
      setLoading(true);
      let patientId = localStorage.getItem("patientId");

      const registerPayload = {
        salutation: normalizeSalutation(localForm.salutation),
        first_name: localForm.firstName,
        middle_name: localForm.middleName,
        last_name: localForm.lastName,
        gender: localForm.gender,
        dob: localForm.dob,
        age: localForm.age,
        mobile_no: verifiedPhoneNumber,
        email: localForm.email,
        address: localForm.address,

        address1: localForm.address1,
        address2: localForm.address2,
        address3: localForm.address3,
        city: localForm.city,
        city_id: localForm.city_id,
        district: localForm.district,
        district_id: localForm.district_id,
        state: localForm.state,
        state_id: localForm.state_id,
        pincode: localForm.pincode,
        country: localForm.country,
        country_id: localForm.country_id,
        marital_status: localForm.maritalStatus,
        patient_id: patientId
      };
      let response;
      let selectedPatient = null;

      if (isAdding) {
        response = await registerPatient(registerPayload);
        patientId = response.patient_id;
        const data = await fetchPatientByPhone(verifiedPhoneNumber);
        localStorage.setItem("patients", JSON.stringify(data?.patients || []));
        selectedPatient = data?.patients?.find(p => p.id === patientId) || null;
        localStorage.setItem("patient", JSON.stringify(selectedPatient));
        localStorage.setItem("patientId", patientId);
        setEditMode(false);
        setIsAdding(false);
        setLocalForm(prev => ({ ...prev, patientId: String(patientId) }));
        setOriginalForm(prev => ({ ...prev, patientId: String(patientId) }));

      }
      else {
        response = await updatePatientDetails(registerPayload);
        const data = await fetchPatientByPhone(verifiedPhoneNumber);
        localStorage.setItem("patients", JSON.stringify(data?.patients || []));

        selectedPatient = data?.patients?.find(p => p.id === Number(patientId)) || null;

        localStorage.setItem("patient", JSON.stringify(selectedPatient));
        setEditMode(false);
        setIsAdding(false);
        // Optionally update originalForm to new saved data
        setLocalForm(prev => ({ ...prev, patientId: String(patientId) }));
        setOriginalForm({ ...localForm });
      }
      onShowOpCard?.(selectedPatient);
      setCollapsed(true);
      setShowShowHide(true);
      toast.success(`${response.message}`);
    } catch (err) {
      console.error("Update failed", err);
    }
    finally {
      setLoading(false);
    }
  };


  const verifiedPhoneNumber = localStorage.getItem("phoneNumber");
  return (
    <div className="relative">
      {/* Show loader on top of the form */}
      {loading && <Loader message={loadingMessage} />}

      <div
        className="relative rounded-2xl p-5 mb-6 transition-all duration-300 glass-card"
        style={{
          borderLeft: isAdding ? "4px solid #10b981" : editMode ? "4px solid #f59e0b" : "4px solid #2563eb",
        }}
      >


        {(editMode || isAdding) && (
          <div className="flex justify-end mb-2">
            <div
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full"
              style={isAdding
                ? { background: isDark ? "#052e16" : "#d1fae5", color: isDark ? "#6ee7b7" : "#065f46", border: `1px solid ${isDark ? "#166534" : "#6ee7b7"}` }
                : { background: isDark ? "#451a03" : "#fef3c7", color: isDark ? "#fcd34d" : "#92400e", border: `1px solid ${isDark ? "#92400e" : "#fcd34d"}` }}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: isAdding ? "#10b981" : "#f59e0b" }}
              />
              {isAdding
                ? t("personal.addMode") || "Adding New Patient"
                : t("personal.editMode") || "Editing"}
            </div>
          </div>
        )}



        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2
    border-b-2 border-sky-400 dark:border-cyan-600 mb-4 pb-2
    rounded-t-lg px-2 py-1 transition overflow-x-hidden"
        >
          <div className="flex flex-col">
            <h3 className="font-extrabold text-base sm:text-lg leading-snug flex flex-wrap items-center gap-2"
                style={{ color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-xl flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #2563eb, #0891b2)" }}
              >
                <FaUserCircle className="text-white" style={{ fontSize: "14px" }} />
              </span>
              {t("personal.title")}
              {form.patientId && (
                <span
                  className="text-[11px] font-mono font-bold px-2 py-0.5 rounded-full"
                  style={{ background: "var(--badge-blue-bg)", color: "var(--badge-blue-text)", border: "1px solid var(--badge-blue-border)" }}
                >
                  #{form.patientId}
                </span>
              )}
              {form.patientId && showShowHide && (
                <button
                  type="button"
                  onClick={() => setCollapsed(v => !v)}
                  className="flex items-center gap-1 text-xs font-semibold rounded-lg px-2 py-0.5 transition-all"
                  style={{
                    background: "var(--surface-hover)",
                    color: "var(--text-secondary)",
                    border: "1px solid var(--border)",
                    touchAction: "manipulation",
                  }}
                >
                  {collapsed ? <>Show <span>▼</span></> : <>Hide <span>▲</span></>}
                </button>
              )}
            </h3>


          </div>




          {hasStoredPatients && (
            <div className="flex items-center gap-4">
              {/* ➕ Add New Patient */}
              {!isAdding && !editMode && (
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm font-bold rounded-xl px-3 py-2 transition-all active:scale-95"
                  style={{
                    background: isDark ? "#052e16" : "#d1fae5",
                    color: isDark ? "#6ee7b7" : "#065f46",
                    border: `1px solid ${isDark ? "#166534" : "#6ee7b7"}`,
                    touchAction: "manipulation",
                    minHeight: 40,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(16,185,129,0.2)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "";
                  }}
                  onClick={() => {
                    setIsAdding(true);
                    setEditMode(true);
                    setCollapsed(false);
                    setShowShowHide(false);
                    setErrors({});
                    setLocalForm({
                      salutation: "",
                      firstName: "",
                      middleName: "",
                      lastName: "",
                      gender: "",
                      dob: "",
                      age: "",
                      email: "",
                      maritalStatus: "",
                      address1: "",
                      address2: "",
                      address3: "",
                      city: "",
                      city_id: "",
                      district: "",
                      district_id: "",
                      state: "",
                      state_id: "",
                      country: "",
                      country_id: "",
                      pincode: ""
                    });
                  }}
                  title="Add new patient"
                >
                  <FaPlus />
                  <span>Add Patient</span>
                </button>
              )}
              {/* Edit Existing Patient */}
              {isPrefilled && !editMode && (
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm font-bold rounded-xl px-3 py-2 transition-all active:scale-95"
                  style={{
                    background: "var(--badge-blue-bg)",
                    color: "var(--badge-blue-text)",
                    border: "1px solid var(--badge-blue-border)",
                    touchAction: "manipulation",
                    minHeight: 40,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(37,99,235,0.2)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "";
                  }}
                  onClick={() => {
                    try {
                      const storedPatient = JSON.parse(localStorage.getItem("patient"));

                      if (storedPatient?.patient_editable === false) {
                        toast.info(
                          "Please visit the admin or front desk to edit your details.",
                          { position: "top-center", autoClose: 3000 }
                        );
                        return;
                      }

                      setEditMode(true);
                      setCollapsed(false);
                      setShowShowHide(false);
                    } catch (err) {
                      console.error("Invalid patient data in localStorage", err);
                      setEditMode(true);
                      setCollapsed(false);
                    }
                  }}
                  title="Edit patient"
                >
                  <FaEdit />
                  <span>Edit</span>
                </button>
              )}
            </div>
          )}
          {editMode && (
            <div className="w-full flex justify-center sm:justify-end mt-4">
              <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 font-bold text-sm text-white rounded-xl transition-all active:scale-95"
                  style={{
                    background: "linear-gradient(135deg, #2563eb, #0891b2)",
                    boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
                    padding: "10px 20px",
                    touchAction: "manipulation",
                    minHeight: 44,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 20px rgba(37,99,235,0.4)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,99,235,0.3)";
                  }}
                >
                  <FaSave />
                  {t("personal.save") || "Save"}
                </button>

                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 font-bold text-sm rounded-xl transition-all active:scale-95"
                  style={{
                    background: "var(--surface)",
                    color: "var(--text-secondary)",
                    border: "1.5px solid var(--border-strong)",
                    padding: "10px 20px",
                    touchAction: "manipulation",
                    minHeight: 44,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <FaTimes />
                  {t("personal.cancel") || "Cancel"}
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Collapsed patient summary chip */}
        {collapsed && isPrefilled && localForm.firstName && (
          <div className="flex items-center gap-2 py-2 animate-fadeIn" style={{ minWidth: 0, overflow: "hidden" }}>
            <FaUserCircle style={{ color: "#2563eb", fontSize: "18px", flexShrink: 0 }} />
            <span className="font-semibold text-sm truncate" style={{ color: "var(--text-primary)", minWidth: 0, flex: 1 }}>
              {[localForm.salutation, localForm.firstName, localForm.middleName, localForm.lastName].filter(Boolean).join(" ")}
            </span>
            {localForm.age && (
              <span className="text-xs flex-shrink-0" style={{ color: "var(--text-muted)" }}>
                · Age {localForm.age}
              </span>
            )}
            {localForm.gender && (
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                style={{
                  background: localForm.gender === "Male" ? "#dbeafe" : localForm.gender === "Female" ? "#fee2e2" : "var(--surface-hover)",
                  color: localForm.gender === "Male" ? "#1d4ed8" : localForm.gender === "Female" ? "#b91c1c" : "var(--text-secondary)",
                }}
              >
                {localForm.gender}
              </span>
            )}
          </div>
        )}

        <div
          className={`transition-all duration-500 ease-in-out overflow-hidden ${collapsed
            ? "max-h-0 opacity-0 scale-y-95"
            : "max-h-[5000px] opacity-100 scale-y-100"
            }`}

        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 w-full">

            {/* Salutation */}
            <div className="md:col-span-1 mb-4">
              <label className="label text-gray-700 dark:text-gray-200">{t("personal.salutation")}</label>


              <Select styles={reactSelectStyles(isDark, isReadOnly)}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                name="salutation"
                value={
                  localForm.salutation
                    ? salutationOptions.find(o => o.value === localForm.salutation)
                    : null
                }
                onChange={(selected) => {
                  const value = selected?.value || "";

                  handleFieldChange("salutation", value);
                  validateField("salutation", value);

                  // If cleared → also clear gender
                  if (!value) {
                    handleFieldChange("gender", "");
                    validateField("gender", "");
                    return;
                  }

                  const autoGender = SALUTATION_GENDER_MAP[value];

                  if (autoGender) {
                    handleFieldChange("gender", autoGender);
                    validateField("gender", autoGender);
                  }
                }}


                options={salutationOptions}
                isDisabled={isReadOnly}

                required
                isClearable
                placeholder={t("personal.selectSalutation") || "Select"}
                isSearchable
              />


            </div>

            {/* First Name */}
            <div className="md:col-span-2 mb-4">
              <label className="label text-gray-700 dark:text-gray-200">
                {t("personal.firstName")} <span className="text-red-500">*</span>
              </label>
              <input
                className={`input border rounded-lg px-3 py-2 focus:outline-none
          focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
          ${errors.firstName ? 'border-red-400 bg-red-50 input-shake' : 'border-gray-300 dark:border-gray-600'}
          ${isReadOnly
                    ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : errors.firstName ? '' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}

                name="firstName"
                value={localForm.firstName || ""}
                placeholder={t("personal.firstNamePlaceholder")}
                onChange={(e) => handleFieldChange("firstName", e.target.value)}
                onBlur={() => validateField("firstName", localForm.firstName)}
                required
                {...readOnlyProps}
              />
              {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>}
            </div>

            {/* Middle Name */}
            <div className="md:col-span-2 mb-4">
              <label className="label text-gray-700 dark:text-gray-200">{t("personal.middleName")}</label>
              <input
                className={`input border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none
    focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
    ${isReadOnly
                    ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                name="middleName"
                value={localForm.middleName || ""}
                placeholder={t("personal.middleNamePlaceholder")}
                onChange={(e) => handleFieldChange("middleName", e.target.value)}
                onBlur={() => validateField("middleName", localForm.middleName)}
                {...readOnlyProps}
              />
              {/* {errors.middleName && <p className="text-red-600 text-sm mt-1">{errors.middleName}</p>} */}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 grid-flow-row-dense">
            {/* Last Name */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200">{t("personal.lastName")}</label>
              <input
                className={`input border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none
    focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
    ${isReadOnly
                    ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                name="lastName"
                value={localForm.lastName || ""}
                placeholder={t("personal.lastNamePlaceholder")}
                onChange={(e) => handleFieldChange("lastName", e.target.value)}
                onBlur={() => validateField("lastName", localForm.lastName)}
                {...readOnlyProps}
              />
              {/* {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>} */}
            </div>

            {/* DOB */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200">{t("personal.dob")}</label>
              <input
                type="date"
                className={`input border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none
    focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
    ${isReadOnly
                    ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                name="dob"
                value={localForm.dob || ""}
                onChange={handleDOBChange}

                required
                {...readOnlyProps}
              />
            </div>

            {/* Age (read-only, auto-filled) */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200">{t("personal.age")}</label>
              <input
                type="text"
                className="input bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
              text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 
              transition-colors duration-300 cursor-not-allowed opacity-70"
                name="age"
                value={localForm.age ?? ""}
                placeholder={t("personal.agePlaceholder") || "Auto-calculated from DOB"}
                disabled
              />
              <p className="text-xs italic text-gray-500 dark:text-gray-400 mt-1">
                {t("personal.hintAutoCalculatedFromDob")}
              </p>

            </div>


            {/* Gender */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200">
                {t("personal.gender")} <span className="text-red-500">*</span>
              </label>
              <Select styles={reactSelectStyles(isDark, isReadOnly)}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                name="gender"
                value={
                  localForm.gender
                    ? { value: localForm.gender, label: localForm.gender }
                    : null
                }
                onChange={(selected) =>
                  handleFieldChange("gender", selected?.value || "")
                }
                options={genders.map((g) => ({
                  value: g.gender_name ?? g,
                  label: g.gender_name ?? g,
                }))}
                isDisabled={isReadOnly}
                isClearable
                isSearchable
                placeholder={t("personal.selectGender")}

              />
              {errors.gender && <p className="text-red-600 text-sm mt-1">{errors.gender}</p>}
            </div>

            {/* Mobile Number */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200 flex items-center gap-1">
                {t("personal.mobileNo")}
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--text-muted)" }}>
                  🔒 Verified
                </span>
              </label>
              <div className="relative">
                <input
                  className="input w-full rounded-xl px-3 py-2 font-mono text-sm cursor-not-allowed"
                  style={{
                    background: "var(--surface-secondary)",
                    border: "1.5px dashed var(--border)",
                    color: "var(--text-secondary)",
                  }}
                  name="mobileNo"
                  value={verifiedPhoneNumber || ""}
                  placeholder={t("personal.mobilePlaceholder")}
                  readOnly
                  tabIndex={-1}
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Linked to your OTP-verified number</p>
              {errors.mobileNo && <p className="text-red-600 text-sm mt-1">{errors.mobileNo}</p>}
            </div>
            {/* Alternate Contact */}
            {Features.altContact && (
              <div>
                <label className="label text-gray-700 dark:text-gray-200">
                  Alternate Contact Number
                </label>
                <input
                  className={`input border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2
      ${isReadOnly ? 'bg-gray-50 dark:bg-gray-700 text-gray-400' : 'bg-white dark:bg-gray-800'}`}
                  name="alternateMobile"
                  value={localForm.alternateMobile || ""}
                  placeholder="Enter alternate number"
                  onChange={(e) => handleFieldChange("alternateMobile", e.target.value)}
                  {...readOnlyProps}
                />
              </div>
            )}
            {/* Email */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200">{t("personal.email")}</label>
              <input
                type="email"
                className={`input border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none
    focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
    ${isReadOnly
                    ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                name="email"
                value={localForm.email || ""}
                placeholder={t("personal.emailPlaceholder")}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                onBlur={() => validateField("email", localForm.email)}
                {...readOnlyProps}
              />
              {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* Marital Status */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200">{t("personal.maritalStatus")}</label>
              <Select styles={reactSelectStyles(isDark, isReadOnly)}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                name="maritalStatus"
                value={
                  localForm.maritalStatus
                    ? { value: localForm.maritalStatus, label: localForm.maritalStatus }
                    : null
                }
                onChange={(selected) =>
                  handleFieldChange("maritalStatus", selected?.value || "")
                }
                options={[
                  { value: "single", label: "Single" },
                  { value: "married", label: "Married" },
                  { value: "divorced", label: "Divorced" },
                  { value: "widowed", label: "Widowed" },
                ]}
                isDisabled={isReadOnly}

                isClearable
                placeholder={t("personal.selectMaritalStatus")}
                isSearchable

              />

            </div>

            {/* Section divider: Address */}
            <div className="md:col-span-2 flex items-center gap-3 pt-2">
              <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Address</span>
              <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label className="label text-gray-700 dark:text-gray-200">
                {t("personal.address")} <span className="text-red-500">*</span>
              </label>
              <div className="md:col-span-2 p-4 rounded-xl border space-y-2"
                   style={{ borderColor: errors.address1 ? "#fca5a5" : "var(--border)", background: errors.address1 ? "#fff5f5" : "var(--surface-secondary)" }}>

                <input
                  name="address1"
                  onBlur={() => validateField("address1", localForm.address1)}
                  required
                  className={`input border rounded-lg px-3 py-2 focus:outline-none
    focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
    ${errors.address1 ? 'border-red-400 bg-red-50 input-shake' : 'border-gray-300 dark:border-gray-600'}
    ${isReadOnly
                      ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                  placeholder={t("personal.address1") || "Address line 1"}
                  value={localForm.address1 || ""}
                  onChange={e => handleFieldChange("address1", e.target.value)}
                  {...readOnlyProps}
                />
                {errors.address1 && <p className="text-red-600 text-sm mt-1">{errors.address1}</p>}
                <input
                  className={`input border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none
    focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
    ${isReadOnly
                      ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                  placeholder={t("personal.address2") || "Address line 2"}
                  value={localForm.address2 || ""}
                  onChange={e => handleFieldChange("address2", e.target.value)}
                  {...readOnlyProps}
                />

                <input
                  className={`input border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none
    focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
    ${isReadOnly
                      ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                  placeholder={t("personal.address3") || "Address line 3"}
                  value={localForm.address3 || ""}
                  onChange={e => handleFieldChange("address3", e.target.value)}
                  {...readOnlyProps}
                />
              </div>
            </div>


            {/* Country */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200">
                {t("personal.country")} <span className="text-red-500">*</span>
              </label>
              <Select styles={reactSelectStyles(isDark, isReadOnly)}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                name="country_id"
                value={
                  localForm.country_id
                    ? {
                      value: localForm.country_id,
                      label: countries.find(c => String(c.country_id) === String(localForm.country_id))?.country_name
                    }
                    : null
                }
                onChange={(selected) => {
                  const id = selected?.value || "";
                  handleFieldChange("country_id", id);
                  const selectedCountry = countries.find(c => String(c.country_id) === String(id));
                  handleFieldChange("country", selectedCountry?.country_name || "");
                  handleFieldChange("state_id", "");
                  handleFieldChange("state", "");
                  handleFieldChange("district_id", "");
                  handleFieldChange("district", "");
                  handleFieldChange("city_id", "");
                  handleFieldChange("city", "");
                  validateField("country_id", id); // validate immediately

                }}
                options={countries.map(c => ({ value: c.country_id, label: c.country_name }))}
                isDisabled={isReadOnly}

                isClearable
                required
                placeholder={t("personal.selectCountry") || "Select Country"}
              />
              {errors.country_id && <p className="text-red-600 text-sm mt-1">{errors.country_id}</p>}

            </div>
            <div>
              <label className="label text-gray-700 dark:text-gray-200">
                {t("personal.state")} <span className="text-red-500">*</span>
              </label>
              <Select styles={reactSelectStyles(isDark, isReadOnly)}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                name="state_id"
                value={
                  localForm.state_id
                    ? {
                      value: localForm.state_id,
                      label: states.find(s => String(s.state_id) === String(localForm.state_id))?.state_name
                    }
                    : null
                }
                onChange={(selected) => {
                  const id = selected?.value || "";
                  handleFieldChange("state_id", id);
                  const selectedState = states.find(s => String(s.state_id) === String(id));
                  handleFieldChange("state", selectedState?.state_name || "");
                  handleFieldChange("district_id", "");
                  handleFieldChange("district", "");
                  handleFieldChange("city_id", "");
                  handleFieldChange("city", "");
                  validateField("state_id", id);
                }}
                options={states
                  .filter(s => !localForm.country_id || String(s.country_id) === String(localForm.country_id))
                  .map(s => ({ value: s.state_id, label: s.state_name }))
                }
                isDisabled={isReadOnly}

                isClearable
                required
                placeholder={t("personal.selectState") || "Select State"}
              />
              {errors.state_id && <p className="text-red-600 text-sm mt-1">{errors.state_id}</p>}

            </div>

            <div>
              <label className="label text-gray-700 dark:text-gray-200">
                {t("personal.district")} <span className="text-red-500">*</span>
              </label>
              <Select styles={reactSelectStyles(isDark, isReadOnly)}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                name="district_id"
                value={
                  localForm.district_id
                    ? {
                      value: localForm.district_id,
                      label: districts.find(d => String(d.district_id) === String(localForm.district_id))?.district_name
                    }
                    : null
                }
                onChange={(selected) => {
                  const id = selected?.value || "";
                  handleFieldChange("district_id", id);
                  const selectedDistrict = districts.find(d => String(d.district_id) === String(id));
                  handleFieldChange("district", selectedDistrict?.district_name || "");
                  handleFieldChange("city_id", "");
                  handleFieldChange("city", "");
                  validateField("district_id", id);

                }}
                options={districts
                  .filter(d => !localForm.state_id || String(d.state_id) === String(localForm.state_id))
                  .map(d => ({ value: d.district_id, label: d.district_name }))
                }
                isDisabled={isReadOnly}

                isClearable
                required
                placeholder={t("personal.selectDistrict") || "Select District"}
              />
              {errors.district_id && <p className="text-red-600 text-sm mt-1">{errors.district_id}</p>}

            </div>
            <div>
              <label className="label text-gray-700 dark:text-gray-200">
                {t("personal.city")} <span className="text-red-500">*</span>
              </label>
              <Select styles={reactSelectStyles(isDark, isReadOnly)}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                name="city_id"
                value={
                  localForm.city_id
                    ? {
                      value: localForm.city_id,
                      label: cities.find(c => String(c.city_id) === String(localForm.city_id))?.city_name
                    }
                    : null
                }
                onChange={(selected) => {
                  const id = selected?.value || "";
                  handleFieldChange("city_id", id);
                  const selectedCity = cities.find(c => String(c.city_id) === String(id));
                  handleFieldChange("city", selectedCity?.city_name || "");
                  validateField("city_id", id);

                }}
                options={cities
                  .filter(c => !localForm.district_id || String(c.district_id) === String(localForm.district_id))
                  .map(c => ({ value: c.city_id, label: c.city_name }))
                }
                isDisabled={isReadOnly}

                isClearable
                required
                placeholder={t("personal.selectCity") || "Select City"}
              />
              {errors.city_id && <p className="text-red-600 text-sm mt-1">{errors.city_id}</p>}

            </div>

            {/* Pincode */}
            <div>
              <label className="label text-gray-700 dark:text-gray-200">
                {t("personal.pincode")} <span className="text-red-500">*</span>
              </label>
              <input
                className={`input border rounded-lg px-3 py-2 focus:outline-none
    focus:ring-2 focus:ring-blue-400 dark:focus:ring-cyan-400 transition-colors duration-300
    ${errors.pincode ? 'border-red-400 bg-red-50 input-shake' : 'border-gray-300 dark:border-gray-600'}
    ${isReadOnly
                    ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : errors.pincode ? '' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                name="pincode"
                value={localForm.pincode || ""}
                placeholder={t("personal.pincodePlaceholder")}
                onChange={(e) => handleFieldChange("pincode", e.target.value)}
                required
                onBlur={() => validateField("pincode", localForm.pincode)}
                {...readOnlyProps}
              />
              {errors.pincode && <p className="text-red-600 text-sm mt-1">{errors.pincode}</p>}
            </div>

            {/* ============================
    ID PROOF
=============================== */}
            <div className="flex flex-col gap-2">

              {/* Label */}
              <label className="label text-gray-700 dark:text-gray-200">
                {t("doctor.IDProof")}
              </label>

              {/* Dropdown */}
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1">
                  <Select
                    styles={reactSelectStyles(isDark, isReadOnly)}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    name="selectedIdType"
                    value={
                      localForm.selectedIdType
                        ? {
                          value: localForm.selectedIdType,
                          label: idProofOptions.find(t => String(t) === String(localForm.selectedIdType)) || localForm.selectedIdType
                        }
                        : null
                    }
                    onChange={(selected) => {
                      const value = selected?.value || "";
                      onChange({ target: { name: "selectedIdType", value } });

                      // auto open file picker after selecting ID type
                      if (value) {
                        setTimeout(() => {
                          fileInputRef.current?.click();
                        }, 100);
                      }
                    }}
                    options={idProofOptions.map(type => ({
                      value: type,
                      label: type
                    }))}
                    isClearable
                    isSearchable
                    isDisabled={!!form.idProof || isReadOnly}
                    className={!!form.idProof ? "opacity-50 cursor-not-allowed" : ""}
                    placeholder={t("doctor.selectIdProof") || "Select"}
                  />
                  {Features.idProofMandatory && errors.idProof && (
                    <p className="text-xs text-red-600 mt-1">{errors.idProof}</p>
                  )}
                </div>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                onChange={handleFileChange}
              />

              {/* Preview / File info card */}
              {form.idProof && (
                <div
                  className="mt-2 p-3 flex items-center gap-3 bg-blue-50 dark:bg-gray-800 rounded-lg shadow-sm border border-blue-200 dark:border-gray-600 transition cursor-pointer"

                >
                  {/* File Icon */}
                  <div onClick={() => setIsModalOpen(true)}>{getFileIcon(form.idProof)}</div>

                  {/* File name */}
                  <div className="text-sm text-gray-800 dark:text-gray-100 break-all flex-1" onClick={() => setIsModalOpen(true)}>
                    {form.idProof}
                  </div>

                  {/* Remove icon */}
                  <button
                    type="button"
                    onClick={() => {
                      onChange({ target: { name: "idProof", value: null } });
                      setIdPreview(null);
                      fileInputRef.current.value = "";
                    }}
                    className="ml-auto p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-700 rounded-full transition"
                    title={t("doctor.remove")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Modal for preview */}
              {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center 
                  bg-black/40 backdrop-blur-sm p-3 sm:p-6">

                  <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl 
                    w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">

                    {/* Preview Area */}
                    <div className="flex-1 p-4 sm:p-6 overflow-auto">
                      {idPreview && form.idProof.toLowerCase().endsWith(".pdf") ? (
                        <iframe
                          src={`${idPreview}#toolbar=0&navpanes=0&scrollbar=0`}
                          title="PDF Preview"
                          className="w-full h-[60vh] sm:h-[70vh] rounded-md border"
                        />
                      ) : (
                        <img
                          src={idPreview}
                          alt="Preview"
                          className="w-full max-h-[70vh] object-contain rounded-md"
                        />
                      )}
                    </div>

                    {/* Bottom Action Bar */}
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold 
                        py-2 rounded-md hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50"
                    >
                      {t("personal.closePreview")}
                    </button>
                  </div>
                </div>
              )}


            </div>

            {errors.idProof && (
              <p className="text-xs text-red-600 mt-1">{errors.idProof}</p>
            )}
            {/* Patient ID */}
            {/* <div>
          <label className="label text-gray-700 dark:text-gray-200">Patient ID</label>
          <input
            type="text"
            className="input bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 
               text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 focus:outline-none 
               transition-colors duration-300"
            name="patientId"
            value={form.patientId ?? ""}
            placeholder="Patient ID"
            {...readOnlyProps}
          />
        </div> */}

          </div>
        </div>
      </div>
    </div>
  );
}
